import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { EthelAccessStore } from '../../store/ethel-access.store';
import { EthelBubbleComponent } from '../ethel-bubble/ethel-bubble.component';
import { EthelDrawerComponent } from '../ethel-drawer/ethel-drawer.component';

/**
 * ADO 149676 (U6 · Ethel Employee Portal Surface), design.md §7.1/§7.8/§7.9.
 *
 * Mounted in `app.html` as a SIBLING of `<router-outlet/>`, gated by
 * `@if (ethelHost.mounted())` — Pattern A (§7.1), matching `SessionTimeoutModalComponent`.
 * Wraps both the bubble and the drawer in `.ethel-root`, the ONE element that declares
 * the G1 D-3 Geist font-family and every `--ethel-*` token (§7.10) — everything beneath
 * it inherits via ordinary CSS custom-property cascade, so no other Ethel component
 * needs to re-import the token partial (`DRY`/`KISS` — see `_ethel-tokens.scss`'s own
 * header comment for why declaring this once here is sufficient).
 *
 * EDGE-E8's "suppression closes the store, not merely hides it" rule lives in
 * `EthelHostService` (a singleton), not here — this component is itself destroyed by the
 * very `@if (ethelHost.mounted())` that condition reacts to, so owning that effect here
 * would race its own teardown.
 *
 * B-4 (code review, production bundle) — everything under `@if (store.open())` is behind
 * `@defer (on interaction(bubble); when store.open())`: the drawer (and everything it
 * composes — header, transcript, message, citation card, composer, both popovers, history
 * panel) is the bulk of the feature and was the ~20 kB gzipped that pushed the initial
 * bundle 38.94 kB over the 5 MB production budget. The BUBBLE stays eager (imported
 * directly, never inside the defer block) — it is the entry point and must paint on first
 * load. `on interaction(bubble)` is the primary trigger, via a template ref on the bubble
 * (`#bubble`) — the SAME click that calls `EthelAccessStore.openDrawer()`, so by the time the
 * deferred chunk loads, `store.open()` is already true and the drawer renders the instant
 * its code arrives. `when store.open()` is a SECOND trigger, not a replacement: today
 * `openDrawer()` has exactly one caller (the bubble's own click), so it is unreachable —
 * but `EthelContextResult.defaultBubbleState` is already on the wire and read by nobody,
 * and any future programmatic open (or a same-tick expand from elsewhere) would otherwise
 * hit `store.open() === true` with the deferred block never triggered — `store.open()`
 * true, drawer rendered false, no error, no loading state, nothing. Angular permits
 * multiple triggers on one `@defer` block; this one costs nothing in bundle size and
 * changes no other behaviour.
 * `.ethel-root` (and every `--ethel-*` token) stays declared HERE, on the eager parent,
 * so the deferred chunk's own styles resolve them via ordinary CSS cascade with no
 * special wiring — verified by the axe fixtures, which render through the real defer
 * block, not a stub.
 *
 * D-4 REVERSED (user, 2026-09-11): the drawer now OVERLAYS page content, matching the
 * Replit reference (`EthelPanel.tsx` — `position: fixed`, no counterpart body/container
 * class, nothing else in that app shifts). The `body.ethel-docked` class toggle and its
 * `padding-right` rule in `styles.scss` are REMOVED, not merely unused — `.ed-panel` was
 * already `position: fixed` on its own geometry, so removing the toggle is sufficient;
 * no replacement rule is needed (`YAGNI`). Accepted consequence, per the user's ruling:
 * below ~2368px viewport width the drawer covers the right portion of page content,
 * including the Disclosures half of the Policies/Disclosures switcher — the same
 * trade-off Replit itself ships with. See design.md §7.6/§10.2 (DEV-2, restated) and
 * gate-decisions.md for the record of both rulings.
 */
@Component({
  selector: 'app-ethel-host',
  standalone: true,
  imports: [EthelBubbleComponent, EthelDrawerComponent, TranslocoModule],
  templateUrl: './ethel-host.component.html',
  styleUrl: './ethel-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EthelHostComponent {
  /** `EthelAccessStore`, not the full `EthelStore` — this component is EAGER (mounted
   *  directly in `app.html`, gated only by `@if (ethelHost.mounted())`, never inside a
   *  `@defer` block), so whatever it injects rides into the initial bundle. It reads only
   *  `store.open()` (the `@defer` trigger guard in its own template) — exactly what the
   *  access store holds (bundle-split follow-up to D-7, gate-decisions.md). */
  protected readonly store = inject(EthelAccessStore);
}
